'use client';

import React, { useEffect, useState } from 'react';
import { AnimatedButton } from './AnimatedButton';
import { DesktopGalleryLoader } from './DesktopGalleryLoader';
import { RESERVATION_URL } from '../lib/site';

export const GallerySection: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    }

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    window.addEventListener('keydown', onEscape);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onEscape);
    };
  }, [menuOpen]);

  const mobileNavItems = [
    { label: 'Accueil', href: '#accueil' },
    { label: 'Soins coiffure', href: '#soins-coiffure' },
    { label: 'Onglerie', href: '#onglerie' },
    { label: 'Salon', href: '#salon' },
    { label: 'Équipe', href: '#equipe' },
    { label: 'Horaires', href: '#horaires' },
    { label: 'Avis', href: '#avis' },
    { label: 'Réservation', href: '#reservation' },
  ];

  return (
    <section className="relative bg-white">
      <h1 className="sr-only">Barber Poitiers</h1>

      <div className="fixed top-0 left-0 right-0 z-50 px-3 pt-3 lg:hidden">
        <div className="mx-auto max-w-6xl rounded-[1.75rem] border border-white/15 bg-[#1d1d1d]/90 px-3 py-2.5 text-white shadow-[0_18px_40px_rgba(0,0,0,0.28)] supports-[backdrop-filter]:bg-[#1d1d1d]/74 supports-[backdrop-filter]:backdrop-blur-lg">
          <div className="flex items-center justify-between gap-3">
            <a href="#accueil" className="flex flex-col leading-none">
              <span className="text-[8px] uppercase tracking-[0.28em] text-white/75 mb-1">Barber</span>
              <span className="text-[13px] uppercase tracking-[0.22em] font-black">Eleganze</span>
            </a>
            <div className="flex items-center gap-2">
              <AnimatedButton
                href={RESERVATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                className="whitespace-nowrap"
              >
                Réserver
              </AnimatedButton>
              <button
                type="button"
                aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                aria-expanded={menuOpen}
                aria-controls="mobile-site-menu"
                onClick={() => setMenuOpen((prev) => !prev)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white"
              >
                {menuOpen ? (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {menuOpen ? (
        <div className="fixed inset-0 z-40 bg-[#1d1d1d]/55 supports-[backdrop-filter]:bg-[#1d1d1d]/42 supports-[backdrop-filter]:backdrop-blur-sm lg:hidden" onClick={() => setMenuOpen(false)}>
          <div
            className="px-3"
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4.75rem)' }}
          >
            <div
              id="mobile-site-menu"
              className="mx-auto max-w-6xl rounded-[1.75rem] border border-white/15 bg-[#1d1d1d]/90 p-4 text-white shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <nav className="flex flex-col">
                {mobileNavItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-2xl px-4 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-white/90 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </div>
      ) : null}

      <div className="pointer-events-none absolute top-[86px] sm:top-[96px] left-0 right-0 z-40 px-4 lg:hidden">
        <p className="mx-auto max-w-6xl text-center text-[10px] sm:text-xs md:text-[13px] uppercase tracking-[0.18em] text-white/90 drop-shadow-[0_3px_10px_rgba(0,0,0,0.6)]">
          Barber Poitiers · Coupe homme · Taille de barbe
        </p>
      </div>

      <div className="lg:hidden stable-screen-height px-4 sm:px-6">
        <div className="mobile-hero-height relative mx-auto mt-24 w-full max-w-[680px] rounded-[1.75rem] overflow-hidden sm:mt-28">
          <img
            src="/images/salon1.webp"
            srcSet="/images/salon1-sm.webp 800w, /images/salon1.webp 1400w"
            sizes="(max-width: 1024px) 100vw, 95vw"
            alt="Vue d'ensemble du salon Eleganze avec ses fauteuils et ses miroirs rétroéclairés"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover brightness-[0.82]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/15 to-black/70" />

          <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 text-white">
            <span className="text-xs sm:text-sm tracking-[0.3em] uppercase opacity-90 block mb-2">
              Eleganze Barber
            </span>
            <div className="text-[13vw] sm:text-[9vw] leading-[0.84] tracking-tight font-black uppercase mb-5 lg:mb-0">
              Barber
              <span className="block">Poitiers</span>
            </div>


            <div className="hidden lg:block h-px w-full bg-white/50 mb-4" />

            <div className="max-w-[280px] rounded-2xl border border-white/20 bg-[#1d1d1d]/72 p-3.5 shadow-[0_20px_50px_rgba(0,0,0,0.22)] sm:max-w-[300px] sm:p-4 supports-[backdrop-filter]:bg-[#1d1d1d]/48 supports-[backdrop-filter]:backdrop-blur-md">
              <div className="flex items-start justify-between gap-3 mb-2.5">
                <div>
                  <div className="text-2xl sm:text-3xl font-black leading-none">4.9<span className="text-base font-bold text-white/60">/5</span></div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-white/75 mt-1">Planity · 9 avis</p>
                </div>
                <div className="flex items-center gap-1 text-amber-300 mt-1" aria-hidden="true">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.8l2.62 5.3 5.85.85-4.24 4.13 1 5.83L12 16.1l-5.23 2.75 1-5.83-4.24-4.13 5.85-.85L12 2.8z"/></svg>
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.8l2.62 5.3 5.85.85-4.24 4.13 1 5.83L12 16.1l-5.23 2.75 1-5.83-4.24-4.13 5.85-.85L12 2.8z"/></svg>
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.8l2.62 5.3 5.85.85-4.24 4.13 1 5.83L12 16.1l-5.23 2.75 1-5.83-4.24-4.13 5.85-.85L12 2.8z"/></svg>
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.8l2.62 5.3 5.85.85-4.24 4.13 1 5.83L12 16.1l-5.23 2.75 1-5.83-4.24-4.13 5.85-.85L12 2.8z"/></svg>
                  <svg className="w-3.5 h-3.5 text-amber-300/50" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.8l2.62 5.3 5.85.85-4.24 4.13 1 5.83L12 16.1l-5.23 2.75 1-5.83-4.24-4.13 5.85.85L12 2.8z"/></svg>
                </div>
              </div>
              <AnimatedButton
                href={RESERVATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full justify-start text-[11px]"
              >
                Réserver une coupe
              </AnimatedButton>
            </div>
          </div>
        </div>
      </div>

      <DesktopGalleryLoader />
    </section>
  );
};
